import { useContext, useMemo, createContext, useCallback } from "react";

import { SlotKey, LineupOption, TeamID } from "../types";

import { ConfirmDialogInfo, TeamFilter } from "./PickerContext";

type PickerStateAction =
  | { type: "RESET_STATE" }
  | {
      type: "PICK_OPTION";
      payload: { slot: SlotKey; options: LineupOption[]; teams: TeamID[] };
    }
  | { type: "CLOSE_CONFIRM_DIALOG" }
  | { type: "SHOW_CONFIRM_DIALOG"; payload: ConfirmDialogInfo }
  | { type: "TOGGLE_TEAM_FILTER"; payload: { teamId: TeamID } };

export const stateReducer = (
  state: PickerState,
  action: PickerStateAction
): PickerState => {
  switch (action.type) {
    case "RESET_STATE":
      return {};
    case "PICK_OPTION":
      return {
        openFor: { slot: action.payload.slot, options: action.payload.options },
        teamFilter: { teams: action.payload.teams },
      };
    case "CLOSE_CONFIRM_DIALOG":
      return {
        ...state,
        showConfirmDialogue: undefined,
      };
    case "TOGGLE_TEAM_FILTER":
      return {
        ...state,
        teamFilter: {
          teams: state.teamFilter?.teams ?? [],
          selected:
            action.payload.teamId === state.teamFilter?.selected
              ? undefined
              : action.payload.teamId,
        },
      };
    case "SHOW_CONFIRM_DIALOG":
      return {
        ...state,
        showConfirmDialogue: action.payload,
      };
  }
};

export interface PickerState {
  openFor?: { slot: SlotKey; options: LineupOption[] };
  teamFilter?: TeamFilter;
  showConfirmDialogue?: ConfirmDialogInfo;
}

export const PickerStateContext = createContext<{
  state: PickerState;
  dispatch: React.Dispatch<PickerStateAction>;
}>({ state: {}, dispatch: () => undefined });

export const usePickerStateSelector = <T,>(
  selector: (state: PickerState) => T
) => {
  const state = useContext(PickerStateContext).state;
  return selector(state)
};

export const usePickerActionDispatch = () => {
  const { dispatch } = useContext(PickerStateContext);
  return useCallback((action: PickerStateAction) => dispatch(action), [dispatch]);
};
